"use client";

import React, { useState } from "react";
import { AnzsicSearchCombobox } from "@/components/ui/AnzsicSearchCombobox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function AnzsicComboboxExample() {
  const [selectedAnzsics, setSelectedAnzsics] = useState<string[]>([]);
  const [singleSelectedAnzsic, setSingleSelectedAnzsic] = useState<string[]>([]);
  
  const handleAnzsicSelect = (value: string) => {
    setSelectedAnzsics(prev => [...prev, value]);
    console.log("ANZSIC selected:", value);
  };
  
  const handleAnzsicDeselect = (value: string) => {
    setSelectedAnzsics(prev => prev.filter(v => v !== value));
    console.log("ANZSIC deselected:", value);
  };
  
  const handleSingleAnzsicSelect = (value: string) => {
    setSingleSelectedAnzsic([value]);
    console.log("Single ANZSIC selected:", value);
  };
  
  const handleSingleAnzsicDeselect = () => {
    setSingleSelectedAnzsic([]);
    console.log("ANZSIC deselected");
  };
  
  const handleClearAll = () => {
    setSelectedAnzsics([]);
    console.log("All ANZSICs cleared");
  };
  
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-8">
      <h1 className="text-3xl font-bold mb-6">ANZSIC Search Combobox</h1>
      
      {/* Multi-select Example */}
      <Card>
        <CardHeader>
          <CardTitle>Multi-select ANZSIC Search</CardTitle>
          <CardDescription>
            Search and select multiple ANZSIC codes. Start typing to search.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <AnzsicSearchCombobox
              selected={selectedAnzsics}
              onSelect={handleAnzsicSelect}
              onDeselect={handleAnzsicDeselect}
              placeholder="Search ANZSIC codes..."
              className="flex-1"
            />
            {selectedAnzsics.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleClearAll}>
                Clear All
              </Button>
            )}
          </div>
          
          {selectedAnzsics.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium mb-2">Selected ANZSIC Codes:</h3>
              <div className="flex flex-wrap gap-2">
                {selectedAnzsics.map(code => (
                  <Badge key={code} variant="secondary" className="text-sm">
                    {code}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Single-select Example */}
      <Card>
        <CardHeader>
          <CardTitle>Single-select ANZSIC Search</CardTitle>
          <CardDescription>
            Search and select a single ANZSIC code.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AnzsicSearchCombobox
            selected={singleSelectedAnzsic}
            onSelect={handleSingleAnzsicSelect}
            onDeselect={handleSingleAnzsicDeselect}
            singleSelect
            placeholder="Select an ANZSIC code..."
          />
          
          {singleSelectedAnzsic.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium mb-2">Selected ANZSIC Code:</h3>
              <div className="text-sm p-3 bg-muted rounded-md">
                {singleSelectedAnzsic[0]}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Usage Instructions */}
      <Card className="bg-muted/50">
        <CardHeader>
          <CardTitle>Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="prose dark:prose-invert max-w-none">
            <h4 className="text-sm font-medium mb-2">Basic Usage:</h4>
            <pre className="bg-black/80 text-white p-4 rounded text-sm overflow-x-auto">
              {`import { AnzsicSearchCombobox } from "@/components/ui/AnzsicSearchCombobox";

function YourComponent() {
  const [selected, setSelected] = useState<string[]>([]);
  
  return (
    <AnzsicSearchCombobox
      selected={selected}
      onSelect={(value) => setSelected(prev => [...prev, value])}
      onDeselect={(value) => setSelected(prev => prev.filter(v => v !== value))}
      placeholder="Search ANZSIC codes..."
    />
  );
}`}
            </pre>
            
            <h4 className="text-sm font-medium mt-4 mb-2">Single Select:</h4>
            <pre className="bg-black/80 text-white p-4 rounded text-sm overflow-x-auto">
              {`<AnzsicSearchCombobox
  selected={selected}
  onSelect={handleSelect}
  onDeselect={handleDeselect}
  singleSelect
  placeholder="Select an ANZSIC code..."
/>`}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
